import { Injectable } from "@angular/core";
import { Storage } from "@ionic/storage";
import AsyncLock from 'async-lock';
import { AlertController, LoadingController, NavController, Platform } from "ionic-angular";
import { Observable, Subject } from "rxjs";
import { Subscription } from "../models/subscription";
import { SubscriptionsPage } from "../pages/subscriptions/subscriptions";
import { Api } from "./api";
import { InAppPurchase } from "@ionic-native/in-app-purchase";
import { UnauthorizedError } from "./api/errors/UnauthorizedError";
import { DirectoryService } from "./directory-service";
import { ErrorResponse } from "./api/responses/ErrorResponse";

@Injectable()
export class SubscriptionService {

    private lock = new AsyncLock();
    private subscription: Subscription;
    private watcher = new Subject<Subscription>();

    constructor(
        private alertCtrl: AlertController,
        private api: Api,
        private inAppPurchase: InAppPurchase,
        private loadingCtrl: LoadingController,
        private platform: Platform,
        private storage: Storage,
    ) {

    }

    public clear() {
        this.set(null);
    }

    public clearAndStore(): Promise<void> {
        this.clear();
        return this.lock.acquire('subscription', async () => {
            await this.storage.remove('subscription');
        });
    }

    public get(): Subscription {
        return this.subscription;
    }

    public read(): Promise<Subscription> {
        return new Promise<Subscription>((resolve, reject) => {
            this.lock.acquire('subscription', async () => {
                let data = await this.storage.get('subscription');
                resolve(data ? Subscription.createOne(data) : null);
            });
        });
    }

    public async readAndSet(): Promise<Subscription> {
        let subscription = await this.read();
        this.set(subscription);
        return subscription;
    }

    public async fetch(): Promise<Subscription> {
        let session = await this.api.getSession().toPromise();
        return session.subscription;
    }

    public store(subscription: Subscription): Promise<void> {
        return this.lock.acquire('subscription', async () => {
            await this.storage.set('subscription', subscription);
        });
    }

    public set(subscription: Subscription): void {
        this.subscription = subscription;
        this.watcher.next(this.subscription);
    }

    public setAndStore(subscription: Subscription): Promise<void> {
        this.set(subscription);
        return this.store(subscription);
    }

    public watch(): Observable<Subscription> {
        return this.watcher.asObservable();
    }

    public showSubscriptionRequired(navCtrl: NavController, subscriptionPage: any) {
        let alert = this.alertCtrl.create({
            title: 'Subscription Required',
            message: 'Sorry, this content is only available with a subscription',
            buttons: [{
                text: 'Cancel'
            }, {
                text: 'Continue',
                handler: () => {
                    this.goToSubscriptionPage(navCtrl, subscriptionPage);
                }
            }]
        });

        alert.present();
    }

    public async goToSubscriptionPage(navCtrl: NavController, subscriptionPage: any) {
        let loading = this.loadingCtrl.create();
        loading.present();

        try {
            let productIds = await this.api.getProductIds().toPromise();
            let productId;

            if (this.platform.is('android')) productId = productIds.product_ids.android;
            if (this.platform.is('ios')) productId = productIds.product_ids.ios;

            let products = await this.inAppPurchase.getProducts([productId]);

            navCtrl.push(subscriptionPage, {
                allowCode: productIds.allow_code,
                product: products[0],
                type: productIds.type
            });
        } catch (error) {
            if (error instanceof ErrorResponse) {
                return this.showError(error.message);
            }

            return this.showError('An unknown error has occurred');
        } finally {
            loading.dismiss();
        }
    }

    public showAlert(title: string, message: string) {
        let alert = this.alertCtrl.create({
            title: title,
            message: message,
            buttons: [{
                text: 'OK'
            }]
        });

        alert.present();
    }

    public showError(message: string) {
        return this.showAlert('Error', message);
    }

}



// WEBPACK FOOTER //
// ./src/providers/subscription-service.ts